import React, { useState } from "react";
import { Grid, Typography, Theme, CardActionArea } from "@material-ui/core";
import { IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/styles";
import { FormattedMessage } from "react-intl";
import { MyPageButton } from "../../../common";
import { useCommonWidths } from "../../../../hooks";
import { FlyOutMenu, FlyOutMenuProps } from "./FlyOutMenuRight";
import { useResource } from "rest-hooks";
import { OptionPairResource } from "../../../../resources";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

const useStyles = makeStyles((theme: Theme) => ({
	headerContainer: {
		paddingLeft: theme.spacing(2),
		paddingRight: 0,
		backgroundColor: "#F2F5F8",
		display: "flex",
	},
	headerContainerText: {
		paddingLeft: theme.spacing(1),
		alignSelf: "center",
	},
	closeIconContainer: {
		textAlign: "center",
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(2),
		[theme.breakpoints.down("sm")]: {
			height: "100%",
			borderTop: "none",
		},
	},
	FAQHeaderText: { color: "#003370", fontWeight: 550 },
	sectionHeaderPaddingBottom: {
		paddingBottom: "24px",
		[theme.breakpoints.down("sm")]: {
			paddingBottom: "12px",
		},
	},
	padding: { padding: 20 },
	borderBottom: {
		borderBottomColor: "gray",
		borderBottomStyle: "solid",
		borderBottomWidth: "1px",
	},
	boldText: { fontWeight: 700 },
	subtitle: {
		paddingTop: 20,
		fontWeight: 750,
		fontSize: 18,
		color: "#003370",
	},
}));

interface HeaderProps {
	toggleMenu: () => void;
	classes: any;
}
const GridHeader = (props: HeaderProps) => {
	const { toggleMenu, classes } = props;
	const isMobile = useCommonWidths().isSmDown;

	return (
		<Grid className={classes.headerContainer} item>
			<Grid item xs={9} className={classes.headerContainerText}>
				<Typography variant={isMobile ? "h6" : "h5"}>
					<FormattedMessage
						id="minside.faq.gridHeader.headerText"
						defaultMessage="Hjelp"
						description="Headertext in FAQ"
					/>
				</Typography>
			</Grid>
			<Grid className={classes.closeIconContainer} item xs={3}>
				<IconButton onClick={() => toggleMenu()}>
					<CloseIcon />
				</IconButton>
			</Grid>
		</Grid>
	);
};

interface FAQProps extends FlyOutMenuProps {
	toggleMenu: () => void;
	open: boolean;
}
export const FAQ = (props: FAQProps) => {
	const classes = useStyles();
	const isMobile = useCommonWidths().isSmDown;

	const faqs = useResource(OptionPairResource.listShape(), {
		name: "MyPageFAQ",
	});

	const faqCategories = faqs
		.map((f) => f.description)
		.filter((value, index, self) => self.indexOf(value) === index)
		.sort();

	return (
		<FlyOutMenu {...props}>
			<GridHeader classes={classes} {...props} />

			<Grid container direction="column" className={classes.padding}>
				<Grid item className={classes.sectionHeaderPaddingBottom}>
					<Typography
						className={classes.FAQHeaderText}
						variant={isMobile ? "h5" : "h4"}
					>
						<FormattedMessage
							id="minside.faq.header"
							defaultMessage="Ofte stilte spørsmål"
							description="frequently asked quetions header"
						/>
					</Typography>
				</Grid>
				<Grid item>
					<Grid container direction="column" spacing={2}>
						{faqCategories.map((category: string) => (
							<FaqCategorySection key={category} categoryName={category}>
								{faqs
									.filter((f) => f.description === category)
									.map((faq: OptionPairResource) => (
										<FaqItem
											key={faq.pk()}
											question={faq.text}
											answer={faq.value}
										/>
									))}
							</FaqCategorySection>
						))}
					</Grid>
				</Grid>
			</Grid>

			<hr />
			<Grid
				spacing={1}
				item
				container
				direction="column"
				className={classes.padding}
			>
				<Grid item>
					<a
						href="https://www.financiering.no/om-oss/bruk-av-cookies/"
						target="_blank"
						rel="noopener noreferrer"
					>
						<MyPageButton nextIcon="chevron">
							<FormattedMessage
								id="minside.faq.privacy_policy"
								defaultMessage="Personvernerklæring"
								description="Privacy policy link text"
							/>
						</MyPageButton>
					</a>
				</Grid>
			</Grid>
			<hr />

			<Grid container direction="column" className={classes.padding}>
				<Grid item className={classes.sectionHeaderPaddingBottom}>
					<Typography
						className={classes.FAQHeaderText}
						variant={isMobile ? "h5" : "h4"}
					>
						<FormattedMessage
							id="minside.faq.customer_service.customer_service_header"
							defaultMessage="Kundeservice"
							description="Customer service header text"
						/>
					</Typography>
				</Grid>
				<Grid spacing={1} container direction="column">
					<Grid
						item
						container
						direction={isMobile ? "column" : "row"}
						spacing={isMobile ? 0 : 3}
						xs={12}
					>
						<Grid item xs={6}>
							<Typography variant="subtitle1" className={classes.boldText}>
								<FormattedMessage
									id="minside.faq.customer_service.email_label"
									defaultMessage="Epost:"
									description="Customer service email label"
								/>
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography>
								<FormattedMessage
									id="minside.faq.customer_service.email"
									defaultMessage="post@financiering.no"
									description="Customer service email address"
								/>
							</Typography>
						</Grid>
					</Grid>

					<Grid
						item
						container
						direction={isMobile ? "column" : "row"}
						spacing={isMobile ? 0 : 3}
						xs={12}
					>
						<Grid item xs={6}>
							<Typography variant="subtitle1" className={classes.boldText}>
								<FormattedMessage
									id="minside.faq.customer_service.phnoe_label"
									defaultMessage="Telefon:"
									description="Customer service phone label"
								/>
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography>
								<FormattedMessage
									id="minside.faq.customer_service.phnoe"
									defaultMessage="400 02 259"
									description="Customer service phone number"
								/>
							</Typography>
						</Grid>
					</Grid>

					<Grid item>
						<Typography className={classes.subtitle}>
							<FormattedMessage
								id="minside.faq.opening_hours.header"
								defaultMessage="Åpningstider"
								description="Opening hours header"
							/>
						</Typography>
					</Grid>

					<Grid
						item
						container
						direction={isMobile ? "column" : "row"}
						spacing={isMobile ? 0 : 3}
						xs={12}
					>
						<Grid item xs={isMobile ? 12 : 6}>
							<Typography variant="subtitle1" className={classes.boldText}>
								Mandag - Fredag
							</Typography>
						</Grid>
						<Grid item xs={isMobile ? 12 : 6}>
							<Typography>08:00 - 16:00</Typography>
						</Grid>
					</Grid>

					<Grid item>
						<Typography className={classes.subtitle}>
							<FormattedMessage
								id="minside.faq.customer_service.adress_header"
								defaultMessage="Adresse"
								description="Adress of customer service"
							/>
						</Typography>
					</Grid>

					<Grid item container direction="column" spacing={0} xs={12}>
						<Grid item>
							<Typography>
								<FormattedMessage
									id="minside.faq.customer_service.physical_address"
									defaultMessage="Roald Amundsens Gate 6, 0161 Oslo"
									description="Address"
								/>
							</Typography>
						</Grid>
						<Grid item>
							<Typography>
								<FormattedMessage
									id="minside.faq.customer_service.PO-box"
									defaultMessage="Postboks 2023 Vika, 0125 Oslo"
									description="PO-box"
								/>
							</Typography>
						</Grid>
						<Grid item>
							<Typography>
								<FormattedMessage
									id="minside.faq.customer_service.organization_number"
									defaultMessage="Org.nr: 911629283"
									description="Organization number"
								/>
							</Typography>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</FlyOutMenu>
	);
};

const useFaqItemStyles = makeStyles((theme: Theme) => ({
	fillRowFlex: {
		flex: 1,
	},
	questionText: {
		fontVariant: "all-petite-caps",
	},
	answerText: {
		color: theme.palette.primary.main,
		marginLeft: theme.spacing(2),
	},
	questionRowBackground: {
		backgroundColor: "#fafafa",
		margin: theme.spacing(1),
	},
}));

interface FaqCategorySectionProps {
	children: any;
	categoryName: string;
}

const FaqCategorySection = (props: FaqCategorySectionProps) => {
	const { children, categoryName } = props;
	const classes = useStyles();
	const isMobile = useCommonWidths().isSmDown;

	return (
		<Grid item container>
			<Grid item xs={12}>
				<Typography
					className={classes.FAQHeaderText}
					variant={isMobile ? "h6" : "h5"}
				>
					{categoryName}
				</Typography>
			</Grid>
			<Grid item>{children}</Grid>
		</Grid>
	);
};

interface FaqItemProps {
	question: string;
	answer: string;
}

const FaqItem = (props: FaqItemProps) => {
	const { question, answer } = props;

	const styles = useFaqItemStyles();

	const [expanded, setExpanded] = useState(false);

	return (
		<Grid item className={styles.fillRowFlex}>
			<CardActionArea onClick={() => setExpanded(!expanded)}>
				<Grid
					container
					direction="row"
					justify="space-between"
					alignItems="center"
					className={styles.questionRowBackground}
				>
					<Grid item className={styles.fillRowFlex}>
						<Typography className={styles.questionText}>{question}</Typography>
					</Grid>
					<Grid item>{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Grid>
				</Grid>
			</CardActionArea>
			{expanded && (
				<Typography className={styles.answerText}>{answer}</Typography>
			)}
		</Grid>
	);
};
